/**
 * Any module.
 * @module src/any
 * @see src/array
 * @see src/object
 */
import { isEmptyArray } from './array';
import { isEmptyObject } from './object';
/**
 * Returns if a value represents a blank value. The blank value can be an null, undefined, an empty string, an empty array or an empty object
 *
 * @function isBlank
 *
 * @param {any} testValue - value to be tested
 * @return {boolean} if the value is blank or not
 *
 * @example
 * isBlank(undefined);
 * // returns true
 *
 * @example
 * isBlank(null);
 * // returns true
 *
 * @example
 * isBlank('');
 * // returns true
 *
 * @example
 * isBlank([]);
 * // returns true
 *
 * @example
 * isBlank({});
 * // returns true
 *
 * @example
 * isBlank(false);
 * // returns true
 *
 * @example
 * isBlank('    ');
 * // returns true
 *
 * @example
 * isBlank('\n\n');
 * // returns true
 *
 * @example
 * isBlank('\r\r');
 * // returns true
 *
 * @example
 * isBlank('\t\t');
 * // returns true
 *
 * @example
 * isBlank('b');
 * // returns false
 *
 * @example
 * isBlank([null]);
 * // returns false
 *
 * @example
 * isBlank({ a: 1 });
 * // returns false
 *
 */

export const isBlank = testValue => {
  const emptStringPattern = /^\p{White_Space}*$/gmu;
  return isEmpty(testValue) || testValue === false || emptStringPattern.test(testValue);
};
/**
 * Returns if a value represents a blank value.
 * The blank value can be an null, undefined, an empty string, an empty array or an empty object
 *
 * @function isEmpty
 *
 * @param {any} testValue - value to be tested
 * @return {boolean} if the value is blank or not
 *
 * Examples:
 *
 * @example
 * isEmpty(undefined);
 * // returns true
 *
 * @example
 * isEmpty(null);
 * // returns true
 *
 * @example
 * isEmpty('');
 * // returns true
 *
 * @example
 * isEmpty([]);
 * // returns true
 *
 * @example
 * isEmpty({});
 * // returns true
 *
 * @example
 * isEmpty(' ');
 * // returns false
 *
 * @example
 * isEmpty('b');
 * // returns false
 *
 * @example
 * isEmpty([null]);
 * // returns false
 *
 * @example
 * isEmpty({ a: 1 });
 * // returns false
 *
 */

export const isEmpty = testValue => {
  return [undefined, null, ''].some(value => value === testValue) || isEmptyArray(testValue) || isEmptyObject(testValue);
};
/**
 * Returns if a value represents a present value. The oposite of isBlank
 *
 * @function isPresent
 *
 * @param {Any} testValue - value to be tested
 * @return {Boolean} - if the value is blank or not
 *
 * @example
 * isPresent(undefined);
 * // returns false
 *
 * @example
 * isPresent(null);
 * // returns false
 *
 * @example
 * isPresent('');
 * // returns false
 *
 * @example
 * isPresent([]);
 * // returns false
 *
 * @example
 * isPresent({});
 * // returns false
 *
 * @example
 * isPresent(false);
 * // returns false
 *
 * @example
 * isPresent('    ');
 * // returns false
 *
 * @example
 * isPresent('\n\n');
 * // returns false
 *
 * @example
 * isPresent('\r\r');
 * // returns false
 *
 * @example
 * isPresent('\t\t');
 * // returns false
 *
 * @example
 * isPresent('b');
 * // returns true
 *
 * @example
 * isPresent([null]);
 * // returns true
 *
 * @example
 * isPresent({ a: 1 });
 * // returns true
 *
 */

export const isPresent = testValue => !isBlank(testValue);
/**
 * Returns the value that is different of the current value.
 *
 * @function toggleBetween
 *
 * @param {any} currentValue - The value to be changed to another one
 * @param {any} firstValue - The first value to be ckecked
 * @param {any} secondValue - The second value to be ckecked
 * @return {any} the value between fist value and sechond value which is different of current value
 *
 * Examples:
 *
 * @example
 * toggleBetween(1, 1, 2);
 * // returns 2
 *
 * @example
 * toggleBetween('rosa', 'azul', 'rosa');
 * // returns 'azul'
 *
 * @example
 * toggleBetween('azul', 'azul', 'rosa');
 * // returns 'rosa'
 *
 */

export const toggleBetween = (currentValue, firstValue, secondValue) => {
  return [firstValue, secondValue].find(value => value !== currentValue);
};